(() => {
  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  if ('IntersectionObserver' in window && !motionPreference.matches) {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('motion-pending');
        entry.target.classList.add('motion-arrived');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.08 });
    const reveal = (scope) => {
      qsa('.lj-walkies__heading, .lj-walkies--product .lj-walkies__copy, .harness-story__panel, .licensed-reviews__card:not([data-extra-review]), .product-card, .image-with-text__content', scope).forEach(el => {
        if (el.getBoundingClientRect().top < window.innerHeight || el.classList.contains('motion-arrived')) return;
        el.classList.add('motion-pending');
        revealObserver.observe(el);
      });
    };
    reveal(document);
    document.addEventListener('shopify:section:load', event => reveal(event.target));
    motionPreference.addEventListener('change', event => {
      if (!event.matches) return;
      revealObserver.disconnect();
      qsa('.motion-pending').forEach(el => el.classList.remove('motion-pending'));
    });
    document.addEventListener('focusin', event => event.target.closest('.motion-pending')?.classList.remove('motion-pending'));
  }

  /* ------------------------------------------------------------------
     Header: expose its height for the mobile menu, close menus on Esc
     ------------------------------------------------------------------ */
  const header = qs('[data-header]');
  const setHeaderBottom = () => {
    if (!header) return;
    const bottom = Math.max(header.getBoundingClientRect().bottom, 0);
    document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
  };
  setHeaderBottom();
  window.addEventListener('resize', setHeaderBottom);
  window.addEventListener('scroll', setHeaderBottom, { passive: true });

  const menuDrawer = qs('[data-menu-drawer]');
  menuDrawer?.addEventListener('toggle', () => {
    setHeaderBottom();
    document.body.classList.toggle('overflow-hidden', menuDrawer.open);
  });

  // Only one header dropdown open at a time; close them when clicking elsewhere.
  document.addEventListener('click', (event) => {
    qsa('[data-header-dropdown][open], [data-header-search][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
  qsa('[data-header-search]').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (details.open) qs('input[type="search"]', details)?.focus();
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    qsa('[data-header-dropdown][open], [data-header-search][open], [data-menu-drawer][open]').forEach((details) => {
      details.removeAttribute('open');
      qs('summary', details)?.focus();
    });
    document.body.classList.remove('overflow-hidden');
    closeCartDrawer();
    closeFacets();
  });

  /* ------------------------------------------------------------------
     Announcement bar rotator
     ------------------------------------------------------------------ */
  qsa('[data-announcement-rotator]').forEach((rotator) => {
    const messages = qsa('.announcement-bar__message', rotator);
    if (messages.length < 2) return;
    let index = 0;
    let paused = false;
    rotator.addEventListener('mouseenter', () => { paused = true; });
    rotator.addEventListener('mouseleave', () => { paused = false; });
    setInterval(() => {
      if (paused) return;
      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    }, 5000);
  });

  /* ------------------------------------------------------------------
     Cart drawer
     ------------------------------------------------------------------ */
  const cartDrawer = () => qs('[data-cart-drawer]');
  let lastFocused = null;
  let cartBusy = false;
  const offers = window.LJCartOffers;
  const refreshDrawer = async () => {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok) throw new Error(strings.cartError);
    renderCartDrawer(await response.text());
  };

  function openCartDrawer() {
    const drawer = cartDrawer();
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    qs('[data-cart-panel]', drawer)?.focus();
  }

  function closeCartDrawer() {
    const drawer = cartDrawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    lastFocused?.focus();
  }

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      badge.classList.add('is-bumped');
    });
  }

  const skippedHolderOffers = new Set();
  function restoreUpsellStage(scope = document) {
    qsa('[data-walk-upsells]', scope).forEach(flow => {
      if (!flow.dataset.offerKey) { skippedHolderOffers.clear(); return; }
      if (!skippedHolderOffers.has(flow.dataset.offerKey)) return;
      const first = qs('[data-holder-step]', flow);
      if (first) first.hidden = true;
      qs('[data-secondary-step]', flow).hidden = false;
    });
  }
  function focusSecondOffer(scope = document) {
    const offer = qs('[data-secondary-step]:not([hidden]) [data-harness-upsell]', scope);
    if (!offer) return;
    const heading = qs('h3', offer);
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
    offer.scrollIntoView({ behavior: motionPreference.matches ? 'auto' : 'smooth', block: 'nearest' });
  }
  restoreUpsellStage();
  document.addEventListener('click', event => {
    const skip = event.target.closest('[data-holder-skip]');
    if (!skip || cartBusy) return;
    const flow = skip.closest('[data-walk-upsells]');
    skippedHolderOffers.add(flow.dataset.offerKey);
    restoreUpsellStage();
    focusSecondOffer(flow);
  });

  // Swap the drawer contents with freshly rendered HTML from the Section Rendering API.
  function renderCartDrawer(html) {
    const drawer = cartDrawer();
    if (!drawer || !html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const freshDrawer = qs('[data-cart-drawer]', fresh);
    if (!freshDrawer) return;
    const previousWalk = qs('[data-shipping-walk]', drawer)?.style.getPropertyValue('--target');
    qs('[data-cart-panel]', drawer).innerHTML = qs('[data-cart-panel]', freshDrawer).innerHTML;
    // Let the free delivery dog walk on from where it was, rather than from the start.
    const walk = qs('[data-shipping-walk]', drawer);
    if (walk && previousWalk) {
      walk.style.setProperty('--from', previousWalk);
      if (previousWalk.trim() === walk.style.getPropertyValue('--target').trim()) walk.removeAttribute('data-moving');
    }
    updateCartCount(Number(freshDrawer.dataset.itemCount || 0));
    restoreUpsellStage(drawer);
  }

  document.addEventListener('change', async event => {
    if (!event.target.matches('[data-bundle-lead], [data-bundle-holder]')) return;
    const editor = event.target.closest('[data-everyday-cart]');
    if (!editor || cartBusy) return;
    cartBusy = true;
    const fields = [...editor.querySelectorAll('select')];
    fields.forEach(field => {field.disabled = true;});
    const feedback = editor.querySelector('[data-bundle-feedback]');
    feedback.textContent = editor.dataset.changing;
    try {
      await window.LindaBundles.swap(editor);
      if (qs('.cart-page')) {window.location.reload();return;}
      await refreshDrawer();
      const replacement = document.querySelector('[data-everyday-cart] [data-bundle-feedback]');
      if (replacement) replacement.textContent = editor.dataset.updated;
    } catch (error) {
      feedback.textContent = error.message || editor.dataset.error;
    } finally {
      cartBusy = false;
      fields.forEach(field => {field.disabled = false;});
    }
  });

  async function changeCartLine(line, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    const drawer = cartDrawer();
    drawer?.classList.add('is-updating');
    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: 'cart-drawer' }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      const changed = await offers?.sync();
      if (changed) await refreshDrawer();
      else renderCartDrawer(data.sections?.['cart-drawer']);
    } catch (error) {
      await refreshDrawer().catch(() => {});
      window.alert(error.message || strings.cartError);
    } finally {
      cartBusy = false;
      drawer?.classList.remove('is-updating');
    }
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle && theme.cartType === 'drawer' && cartDrawer()) {
      event.preventDefault();
      openCartDrawer();
      return;
    }
    if (event.target.closest('[data-cart-close]')) {
      closeCartDrawer();
      return;
    }
    const change = event.target.closest('[data-cart-change]');
    if (change) {
      event.preventDefault();
      changeCartLine(Number(change.dataset.line), Math.max(Number(change.dataset.quantity), 0));
    }
  });

  /* ------------------------------------------------------------------
     Add to cart (product page + quick add)
     ------------------------------------------------------------------ */
  // When a quantity-break tier with per-item variant pickers is selected, return the
  // cart lines to add (e.g. 1 x Red + 1 x Blue). Returns null when the normal form data applies.
  function getBundleItems(form) {
    const scope = form.closest('[data-product-section]');
    const tier = scope && qs('.qty-break__input:checked', scope)?.closest('.qty-break');
    const selects = tier ? qsa('[data-qty-variants] select', tier) : [];
    if (!selects.length) return null;

    const counts = new Map();
    selects.forEach((select) => counts.set(select.value, (counts.get(select.value) || 0) + 1));
    const sellingPlan = new FormData(form).get('selling_plan');
    return Array.from(counts, ([id, quantity]) => ({
      id: Number(id),
      quantity,
      ...(sellingPlan ? { selling_plan: Number(sellingPlan) } : {}),
    }));
  }

  document.addEventListener('change', event => {
    if (!event.target.matches('[data-patch-toggle]')) return;
    const patch = event.target.closest('[data-patch]');
    patch.querySelector('[data-patch-fields]').hidden = !event.target.checked;
    patch.querySelector('[data-patch-text]').required = event.target.checked;
    patch.querySelector('[data-patch-text]').setCustomValidity('');
    const status = patch.querySelector('[data-patch-status]');
    if (status) status.textContent = event.target.checked ? status.dataset.selectedLabel : status.dataset.optionalLabel;
    if (event.target.checked) { patch.open = true; patch.querySelector('[data-patch-text]').focus(); }
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-patch-text]')) event.target.setCustomValidity('');
  });
  const initCountdowns = root => root.querySelectorAll('[data-sale-countdown]').forEach(timer => {
    if (timer.dataset.connected) return;
    timer.dataset.connected = 'true';
    const deadline = Date.parse(timer.dataset.saleCountdown);
    if (!Number.isFinite(deadline)) return;
    let interval;
    const update = () => {
      const seconds = Math.floor((deadline - Date.now()) / 1000);
      if (seconds <= 0) { timer.hidden = true; clearInterval(interval); return; }
      timer.hidden = false;
      const hours = Math.floor(seconds / 3600);
      timer.querySelector('[data-countdown-output]').textContent = [hours, Math.floor(seconds % 3600 / 60), seconds % 60].map(value => String(value).padStart(2, '0')).join(':');
    };
    update();
    if (!timer.hidden) interval = setInterval(update, 1000);
    document.addEventListener('shopify:section:unload', event => { if (event.target.contains(timer)) clearInterval(interval); });
  });
  initCountdowns(document);
  document.addEventListener('shopify:section:load', event => initCountdowns(event.target));

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    if (form.closest('[data-product-section]')?.dataset.safetyHold) { event.preventDefault(); return; }
    if (form.dataset.submitting === 'true') { event.preventDefault(); return; }
    if (cartBusy) { event.preventDefault(); return; }
    const productSection = form.closest('[data-product-section]');
    if (productSection?.hasAttribute('data-require-selection')) {
      const fields = qsa('[data-variant-picker] fieldset', productSection);
      if (fields.some(field => !qs('input:checked', field)) || !qs('[data-variant-id]', productSection)?.value) {
        event.preventDefault();
        qs('input', fields.find(field => !qs('input:checked', field)) || productSection)?.focus();
        return;
      }
    }
    let bundleItems = getBundleItems(form);
    const inlineOffers = productSection?.querySelector('[data-inline-offers]');
    if (inlineOffers && productSection.dataset.inlineOffer === 'everyday') {
      const bundleVariant = Number(inlineOffers.dataset.bundleVariant);
      if (!bundleVariant) { event.preventDefault(); return; }
      bundleItems = [{ id: bundleVariant, quantity: 1 }];
    }
    const patch = form.closest('[data-product-section]')?.querySelector('[data-patch]');
    const patchSelected = patch?.querySelector('[data-patch-toggle]')?.checked;
    if (patchSelected) {
      const textField = patch.querySelector('[data-patch-text]');
      const text = textField.value.trim();
      if (!text) {
        patch.open = true;
        event.preventDefault();
        textField.setCustomValidity(strings.patchRequired);
        textField.reportValidity();
        textField.focus();
        return;
      }
      const data = new FormData(form);
      if (!bundleItems) bundleItems = [{ id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1), ...(data.get('selling_plan') ? { selling_plan: Number(data.get('selling_plan')) } : {}) }];
      const quantity = bundleItems.reduce((sum, item) => sum + item.quantity, 0);
      const linkedVariants = bundleItems.map(item => item.id).join(',');
      bundleItems = [...bundleItems.map(item => ({ ...item, properties: { ...item.properties, 'Custom patch': text } })), { id: Number(patch.dataset.patchVariant), quantity, properties: { 'Patch text': text, '_Harness variants': linkedVariants } }];
    }
    const useDrawer = theme.cartType === 'drawer' && !!cartDrawer();
    // Without a drawer, plain forms submit normally; bundles always need the AJAX API.
    if (!useDrawer && !bundleItems && !offers?.active) return;
    event.preventDefault();
    form.dataset.submitting = 'true';
    cartBusy = true;

    const submitter = event.submitter || qs('[type="submit"]', form);
    const scope = form.closest('[data-product-section]') || form;
    const errorEl = qs('[data-form-error]', scope);
    const buttons = qsa(`[form="${form.id}"], [type="submit"]`, document).filter(
      (button) => button.form === form
    );

    buttons.forEach((button) => button.setAttribute('aria-disabled', 'true'));
    submitter?.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;

    let body;
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (bundleItems) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({
        items: bundleItems,
        ...(useDrawer ? { sections: 'cart-drawer', sections_url: window.location.pathname } : {}),
      });
    } else {
      body = new FormData(form);
      if (body.get('selling_plan') === '') body.delete('selling_plan');
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${routes.cart_add_url}.js`, { method: 'POST', headers, body });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message);
      const giftChanged = await offers?.sync();
      if (!useDrawer) {
        window.location.href = routes.cart_url;
        return;
      }
      if (giftChanged) await refreshDrawer();
      else renderCartDrawer(data.sections?.['cart-drawer']);
      openCartDrawer();
    } catch (error) {
      if (useDrawer) await refreshDrawer().catch(() => {});
      const message = error.message || strings.cartError;
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        window.alert(message);
      }
    } finally {
      cartBusy = false;
      delete form.dataset.submitting;
      buttons.forEach((button) => button.removeAttribute('aria-disabled'));
      submitter?.classList.remove('is-loading');
    }
  });


  document.addEventListener('change', event => {
    if (!event.target.matches('[data-harness-option]')) return;
    const card = event.target.closest('[data-harness-upsell]');
    const variants = JSON.parse(qs('[data-harness-upsell-variants]', card).textContent);
    const selects = qsa('[data-harness-option]', card);
    const selected = selects.map(select => select.value);
    const variant = selected.every(Boolean) ? variants.find(item => item.options.every((value, index) => value === selected[index])) : null;
    const button = qs('[data-addon-add]', card);
    const hiddenSelect = qs('[data-addon-variant]', card);
    hiddenSelect.innerHTML = '';
    const selectedOption = document.createElement('option');
    selectedOption.value = variant?.available ? String(variant.id) : '';
    hiddenSelect.append(selectedOption);
    button.disabled = !variant?.available;
    button.textContent = variant?.available ? 'Add second harness · 50% off' : selected.every(Boolean) ? 'Unavailable — choose another size' : 'Choose colour & size';
    const preview = variant || variants.find(item => item.available && item.options[0] === selected[0]);
    if (preview) {
      qs('[data-addon-price]', card).textContent = preview.price;
      qs('[data-addon-compare]', card).textContent = preview.compare;
      const image = qs('.cart-harness-offer__image', card);
      if (image && preview.image) { image.src = preview.image; image.removeAttribute('srcset'); image.alt = `${preview.options[0]} no pull dog harness`; }
    }
    qs('[data-addon-error]', card).hidden = true;
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-addon-variant]')) return;
    const card = event.target.closest('[data-cart-addon]');
    const option = event.target.selectedOptions[0];
    if (card.matches('[data-holder-upsell]')) qs('[data-addon-add]', card).disabled = !option.value || option.disabled;
    qs('[data-addon-price]', card).textContent = option.dataset.price;
    const compare = qs('[data-addon-compare]', card);
    compare.textContent = option.dataset.compare;
    compare.hidden = !option.dataset.compare;
    const image = qs('img', card);
    if (image && option.dataset.image) { image.src = option.dataset.image; image.removeAttribute('srcset'); }
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-addon-add]');
    if (!button) return;
    event.preventDefault();
    if (cartBusy) return;
    cartBusy = true;
    button.disabled = true;
    const card = button.closest('[data-cart-addon]');
    const errorEl = qs('[data-addon-error]', card);
    const addonId = Number(qs('[data-addon-variant]', card).value);
    if (!addonId) { button.disabled = true; cartBusy = false; return; }
    errorEl.hidden = true;
    const drawer = cartDrawer();
    drawer?.classList.add('is-updating');
    try {
      if (card.matches('[data-harness-upsell]') && offers?.addHarness) {
        await offers.addHarness(Number(qs('[data-addon-variant]', card).value));
        if (button.closest('.cart-page')) window.location.reload();
        else await refreshDrawer();
        return;
      }
      const response = await fetch(`${routes.cart_add_url}.js`, { method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({items:[{id:Number(qs('[data-addon-variant]', card).value), quantity:1}], sections:'cart-drawer'}) });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      const changed = await offers?.sync();
      if (button.closest('.cart-page')) window.location.reload();
      else if (changed) await refreshDrawer();
      else renderCartDrawer(data.sections?.['cart-drawer']);
      if (card.matches('[data-holder-upsell]') && drawer) focusSecondOffer(drawer);
    } catch(error) {
      errorEl.textContent = error.message || strings.cartError;
      errorEl.hidden = false;
    } finally {
      cartBusy = false;
      button.disabled = false;
      drawer?.classList.remove('is-updating');
    }
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-pair-keep-first]');
    if (!button || !offers?.keepFirst) return;
    event.preventDefault();
    if (cartBusy) return;
    cartBusy = true;
    button.disabled = true;
    try {
      await offers.keepFirst(button.dataset.pairKeepFirst);
      if (button.closest('.cart-page')) window.location.reload();
      else await refreshDrawer();
    } catch (error) { window.alert(error.message || strings.cartError); }
    finally { cartBusy = false; button.disabled = false; }
  });
  document.addEventListener('change', async event => {
    const select = event.target.closest('[data-gift-variant]');
    if (!select || cartBusy || !offers?.active) return;
    cartBusy = true;
    const selected = select.value;
    select.disabled = true;
    try {
      await offers.choose(select.dataset.giftKey, selected);
      if (select.closest('.cart-page')) window.location.reload();
      else await refreshDrawer();
    } catch (error) {
      if (select.closest('.cart-page')) window.location.reload();
      else await refreshDrawer().catch(() => {});
      window.alert(error.message || strings.cartError);
    } finally { cartBusy = false; select.disabled = false; }
  });
  let offerReady = Promise.resolve();
  if (offers?.active) {
    cartBusy = true;
    offerReady = offers.sync().then(async changed => {
      if (!changed) return;
      if (qs('.cart-page')) window.location.reload();
      else await refreshDrawer();
    }).catch(error => {window.alert(error.message || strings.cartError);}).finally(() => {cartBusy = false;});
    document.addEventListener('submit', async event => {
      if (event.target.matches('[data-type="add-to-cart-form"]')) return;
      if (!event.submitter?.matches('[name="checkout"]')) return;
      event.preventDefault();
      const form = event.target;
      const submitter = event.submitter;
      await offerReady;
      if (cartBusy) return;
      cartBusy = true;
      submitter.disabled = true;
      try {
        await offers.sync();
        if (form.matches('[data-cart-form]')) {
          const payload = new FormData(form);
          const response = await fetch(`${routes.cart_url}/update.js`, {method:'POST', body:payload});
          if (!response.ok) throw new Error(strings.cartError);
          await offers.sync();
        }
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      } catch(error) {window.alert(error.message || strings.cartError);}
      finally {cartBusy = false; submitter.disabled = false;}
    });
  }

  /* ------------------------------------------------------------------
     Quantity steppers (product form + cart page)
     ------------------------------------------------------------------ */
  let cartSubmitTimer;
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-quantity-step]');
    if (!step) return;
    const input = qs('[data-quantity-input]', step.closest('[data-quantity]'));
    if (!input) return;
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + Number(step.dataset.quantityStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-autosubmit]');
    if (!input) return;
    clearTimeout(cartSubmitTimer);
    cartSubmitTimer = setTimeout(() => input.form?.submit(), 500);
  });

  /* ------------------------------------------------------------------
     Product page
     ------------------------------------------------------------------ */
  qsa('[data-product-section]').forEach(initProduct);

  function initProduct(section) {
    const variantsEl = qs('[data-variants-json]', section);
    const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    const picker = qs('[data-variant-picker]', section);
    const idInput = qs('[data-variant-id]', section);
    const sectionId = section.dataset.sectionId;
    const productUrl = section.dataset.productUrl;
    const slider = qs('[data-product-slider]', section);
    const requireSelection = section.hasAttribute('data-require-selection');
    const chooseOptions = section.dataset.chooseOptions || strings.chooseOptions;
    if (requireSelection) {
      qsa('input[type=radio]', picker).forEach(input => {input.checked = false;});
      if (idInput) idInput.value = '';
      qsa('[data-add-button]', section).forEach(button => {button.disabled = true; button.textContent = chooseOptions;});
    }

    const thumbs = qsa('[data-thumb]', section);
    const slides = slider ? qsa('[data-media-id]', slider) : [];
    const visibleSlides = () => slides.filter(slide => !slide.hidden);
    const markActiveMedia = (mediaId) => {
      thumbs.forEach(thumb => {
        const active = thumb.dataset.thumb === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };
    const filterGallery = (colour) => {
      const key = String(colour || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      if (!slides.some(slide => slide.dataset.mediaColor === key)) return;
      slides.forEach(slide => { slide.hidden = slide.dataset.mediaColor !== key; });
      thumbs.forEach(thumb => { thumb.hidden = thumb.dataset.mediaColor !== key; });
      section.dataset.galleryColour = key;
      slider.scrollLeft = 0;
    };
    const showMedia = (mediaId, behavior = 'smooth') => {
      const slide = visibleSlides().find(item => item.dataset.mediaId === String(mediaId)) || visibleSlides()[0];
      if (!slide) return;
      slider.scrollTo({ left: slide.offsetLeft - slider.offsetLeft, behavior });
      markActiveMedia(slide.dataset.mediaId);
    };
    thumbs.forEach(thumb => thumb.addEventListener('click', () => showMedia(thumb.dataset.thumb)));
    if (slides.some(slide => slide.dataset.mediaColor)) {
      filterGallery(section.dataset.galleryColour || visibleSlides()[0]?.dataset.mediaColor);
      showMedia(undefined, 'auto');
    }
    slider?.addEventListener('scroll', () => {
      if (!slider.clientWidth) return;
      const visible = visibleSlides();
      const index = Math.max(0, Math.min(visible.length - 1, Math.round(slider.scrollLeft / slider.clientWidth)));
      const slide = visible[index];
      if (slide) markActiveMedia(slide.dataset.mediaId);
    }, { passive: true });

    // Quantity breaks set the form quantity (including the preselected tier on load).
    const qtyInput = qs('[data-quantity-input]', section);
    const initialTier = qs('.qty-break__input:checked', section);
    if (initialTier && qtyInput) qtyInput.value = initialTier.value;
    section.addEventListener('change', (event) => {
      if (event.target.matches('.qty-break__input') && qtyInput) {
        qtyInput.value = event.target.value;
      }
    });

    // Variant selection.
    let requestId = 0;
    picker?.addEventListener('change', async () => {
      const currentRequest = ++requestId;
      const fieldsets = qsa('fieldset', picker);
      const selected = fieldsets.map((fieldset) => qs('input:checked', fieldset)?.value);
      fieldsets.forEach((fieldset, index) => {
        const label = qs('[data-selected-value]', fieldset);
        if (label) label.textContent = selected[index] || strings.chooseValue;
      });

      const variant = variants.find((v) => v.options.every((option, i) => option === selected[i]));

      // Colour previews work before size is chosen, without selecting a cart variant.
      const colourIndex = fieldsets.findIndex(fieldset => /^(color|colour)$/i.test(fieldset.dataset.optionName || ''));
      const previewIndex = colourIndex >= 0 ? colourIndex : (requireSelection ? 0 : -1);
      if (previewIndex >= 0 && selected[previewIndex] !== undefined) {
        const previewVariant = variant || variants.find(v =>
          v.options[previewIndex] === selected[previewIndex] && v.featured_media
        );
        filterGallery(selected[previewIndex]);
        if (previewVariant?.featured_media) showMedia(previewVariant.featured_media.id, 'auto');
      }

      // Mark values that can't combine with the current selection.
      fieldsets.forEach((fieldset, optionIndex) => {
        qsa('input', fieldset).forEach((input) => {
          const possible = variants.some(
            (v) =>
              v.available &&
              v.options[optionIndex] === input.value &&
              v.options.every((option, i) => i === optionIndex || selected[i] === undefined || option === selected[i])
          );
          input.nextElementSibling?.classList.toggle('pill--unavailable', !possible);
        });
      });

      const addButtons = qsa('[data-add-button]', section);
      const stickyVariant = qs('[data-sticky-variant]', section);
      if (stickyVariant) stickyVariant.textContent = variant ? (variant.title === 'Default Title' ? '' : variant.title) : selected.filter(Boolean).join(' / ');
      offers?.display(section, variant);
      if (!variant) {
        if (idInput) idInput.value = '';
        addButtons.forEach((button) => {
          button.disabled = true;
          button.textContent = selected.some(value => value === undefined) ? chooseOptions : strings.unavailable;
        });
        return;
      }

      if (idInput) idInput.value = variant.id;
      addButtons.forEach((button) => {
        button.disabled = !variant.available || Boolean(section.dataset.safetyHold);
        button.textContent = variant.available ? strings.addToCart : strings.soldOut;
      });
      if (variant.featured_media) showMedia(variant.featured_media.id, 'auto');
      const variantUrl = new URL(location.href); variantUrl.searchParams.set('variant', variant.id); window.history.replaceState({}, '', variantUrl);

      // Re-render price, stock and quantity-break blocks for the new variant.
      try {
        const response = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`);
        const html = await response.text();
        if (currentRequest !== requestId) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        qsa('[data-variant-update]', section).forEach((el) => {
          const replacement = qs(`[data-variant-update="${el.dataset.variantUpdate}"]`, fresh);
          if (!replacement) return;
          // Keep the chosen quantity break selected.
          const checked = qs('input:checked', el)?.value;
          el.innerHTML = replacement.innerHTML;
          if (checked) {
            const input = qs(`input[value="${checked}"]`, el);
            if (input) input.checked = true;
          }
        });
      } catch (error) {
        // Price display stays as-is; the cart will still receive the correct variant.
      }
    });

    // Sticky add to cart appears once the main button scrolls out of view.
    const sticky = qs('[data-sticky-atc]', section);
    const mainButton = qs('.product-form__submit', section);
    if (sticky && mainButton) {
      let stickyFrame = 0;
      const updateSticky = () => {
        stickyFrame = 0;
        const show = mainButton.getBoundingClientRect().bottom <= 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        qs('button', sticky)?.setAttribute('tabindex', show ? '0' : '-1');
      };
      const scheduleSticky = () => {
        if (!stickyFrame) stickyFrame = requestAnimationFrame(updateSticky);
      };
      const stickyObserver = 'IntersectionObserver' in window
        ? new IntersectionObserver(scheduleSticky)
        : null;
      stickyObserver?.observe(mainButton);
      window.addEventListener('scroll', scheduleSticky, { passive: true });
      window.addEventListener('resize', scheduleSticky, { passive: true });
      updateSticky();
      const unloadSticky = (event) => {
        if (event.target !== section && !event.target.contains(section)) return;
        stickyObserver?.disconnect();
        window.removeEventListener('scroll', scheduleSticky);
        window.removeEventListener('resize', scheduleSticky);
        if (stickyFrame) cancelAnimationFrame(stickyFrame);
        document.removeEventListener('shopify:section:unload', unloadSticky);
      };
      document.addEventListener('shopify:section:unload', unloadSticky);
    }
  }

  /* ------------------------------------------------------------------
     Breed size slider
     ------------------------------------------------------------------ */
  qsa('[data-size-slider]').forEach(initSizeSlider);
  document.addEventListener('shopify:section:load', (event) => {
    qsa('[data-size-slider]', event.target).forEach(initSizeSlider);
  });
  // In the theme editor, open the pop-up version while its block is selected.
  document.addEventListener('shopify:block:select', (event) => {
    const launch = event.target.closest?.('[data-size-slider-launch]');
    document.getElementById(launch?.dataset.sizeSliderLaunch)?.showModal();
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const launch = event.target.closest?.('[data-size-slider-launch]');
    document.getElementById(launch?.dataset.sizeSliderLaunch)?.close();
  });

  function initSizeSlider(root) {
    const range = qs('[data-size-slider-range]', root);
    const sizeButtons = qsa('[data-size-label]', root);
    const sizes = sizeButtons.map((button) => ({
      button,
      label: button.dataset.sizeLabel,
      value: button.dataset.sizeValue,
      min: Number(button.dataset.sizeMin),
      max: Number(button.dataset.sizeMax),
    }));
    if (!range || !sizes.length) return;

    // Sizes share boundaries (e.g. Small to 50, Medium from 50): a boundary belongs to the larger size.
    const sizeAt = (value) => {
      let match = 0;
      sizes.forEach((size, index) => {
        if (value >= size.min) match = index;
      });
      return match;
    };
    const breeds = qsa('[data-breed-chest]', root).map((el) => {
      const chest = Number(el.dataset.breedChest);
      return { el, chest, name: el.dataset.breedName, size: sizeAt(chest) };
    });

    const section = root.closest('[data-product-section]');
    const optionIndex = Number(root.dataset.optionIndex);
    const optionName = `Option-${root.dataset.sectionId}-${optionIndex}`;
    const valueEl = qs('[data-size-slider-value]', root);
    const sizeEl = qs('[data-size-slider-size]', root);
    const breedEl = qs('[data-size-slider-breed]', root);
    const selectButton = qs('[data-size-slider-select]', root);
    const selectSizeEl = qs('[data-size-slider-select-size]', root);
    const unit = root.dataset.unit;

    const update = () => {
      const value = Number(range.value);
      const sizeIndex = sizeAt(value);
      const size = sizes[sizeIndex];

      // Show the closest breed that falls in the same size, so the picture never contradicts the size.
      const pool = breeds.some((breed) => breed.size === sizeIndex)
        ? breeds.filter((breed) => breed.size === sizeIndex)
        : breeds;
      const breed = pool.reduce(
        (best, candidate) => (!best || Math.abs(candidate.chest - value) < Math.abs(best.chest - value) ? candidate : best),
        null
      );
      breeds.forEach((b) => {
        b.el.classList.toggle('is-active', b === breed);
        if (b !== breed) b.el.classList.remove('is-hopping');
      });
      sizes.forEach((s, i) => s.button.classList.toggle('is-active', i === sizeIndex));

      if (valueEl) valueEl.textContent = value;
      if (sizeEl) sizeEl.textContent = size.label;
      if (selectSizeEl) selectSizeEl.textContent = size.label;
      if (breedEl) breedEl.textContent = breed ? breed.name : '';
      const position = ((value - Number(range.min)) / (Number(range.max) - Number(range.min))) * 100;
      range.style.setProperty('--position', `${position}%`);
      range.setAttribute('aria-valuetext', `${value} ${unit}, ${size.label}${breed ? `, ${breed.name}` : ''}`);
      return sizeIndex;
    };

    const selectVariant = (sizeIndex) => {
      if (!section || optionIndex < 0) return;
      const input = qsa(`input[name="${optionName}"]`, section).find((el) => el.value === sizes[sizeIndex].value);
      if (!input || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const moveToSize = (sizeIndex) => {
      const size = sizes[sizeIndex];
      range.value = (size.min + size.max) / 2;
      update();
    };

    // Tapping the dog makes it hop and say something.
    const stage = qs('[data-size-slider-stage]', root);
    const bubble = qs('[data-size-slider-bubble]', root);
    const reactions = (stage?.dataset.reactions || '').split(',').map((text) => text.trim()).filter(Boolean);
    const restart = (el, className) => {
      el.classList.remove(className);
      void el.offsetWidth; // Reflow so the animation plays again on repeat taps.
      el.classList.add(className);
    };
    stage?.addEventListener('click', () => {
      const dog = qs('.size-slider__dog.is-active', root);
      if (!dog) return;
      restart(dog, 'is-hopping');

      if (!bubble || !reactions.length) return;
      const svg = qs('svg[data-head-x]', dog);
      bubble.style.left = `${svg ? svg.dataset.headX : 60}%`;
      bubble.style.top = `${svg ? svg.dataset.headY : 15}%`;
      bubble.textContent = reactions[Math.floor(Math.random() * reactions.length)];
      restart(bubble, 'is-visible');
    });

    // Pop-up style: the button confirms the size (the dialog itself is closed by data-dialog-close).
    selectButton?.addEventListener('click', () => selectVariant(sizeAt(Number(range.value))));

    range.addEventListener('input', update);
    range.addEventListener('change', () => selectVariant(update()));
    sizes.forEach((size, index) => {
      size.button.addEventListener('click', () => {
        moveToSize(index);
        selectVariant(index);
      });
    });

    // Picking a size in the variant picker moves the slider into that size.
    section?.addEventListener('change', (event) => {
      if (event.target.name !== optionName) return;
      const index = sizes.findIndex((size) => size.value === event.target.value);
      if (index !== -1 && index !== sizeAt(Number(range.value))) moveToSize(index);
    });

    const selected = optionIndex >= 0 && section && qs(`input[name="${optionName}"]:checked`, section);
    const selectedIndex = selected ? sizes.findIndex((size) => size.value === selected.value) : -1;
    if (selectedIndex !== -1) {
      moveToSize(selectedIndex);
    } else {
      update();
    }
  }

  /* ------------------------------------------------------------------
     Dialogs (size guide)
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      document.getElementById(opener.dataset.dialogOpen)?.showModal();
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target.tagName === 'DIALOG') event.target.close();
  });

  /* ------------------------------------------------------------------
     Collection filters
     ------------------------------------------------------------------ */
  const facetsPanel = () => qs('[data-facets-panel]');
  function closeFacets() {
    const panel = facetsPanel();
    if (!panel?.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    qsa('.facets__toggle').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
  }
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-facets-toggle]')) return;
    const panel = facetsPanel();
    if (!panel) return;
    const open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    document.body.classList.toggle('overflow-hidden', open);
    qsa('.facets__toggle').forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-facets-autosubmit]')) return;
    // On mobile the panel has an Apply button, so only auto-submit on desktop or for sorting.
    if (event.target.name === 'sort_by' || window.matchMedia('(min-width: 990px)').matches) {
      event.target.form?.submit();
    }
  });

  /* ------------------------------------------------------------------
     Product recommendations
     ------------------------------------------------------------------ */
  qsa('[data-recommendations]').forEach(async (container) => {
    if (container.children.length) return;
    try {
      const response = await fetch(container.dataset.url);
      const html = await response.text();
      const fresh = qs('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
      if (fresh?.innerHTML.trim()) container.innerHTML = fresh.innerHTML;
    } catch (error) {
      // Recommendations are optional.
    }
  });

  /* ------------------------------------------------------------------
     Confirm before destructive forms (e.g. delete address)
     ------------------------------------------------------------------ */
  document.addEventListener('submit', (event) => {
    const message = event.target.dataset?.confirm;
    if (message && !window.confirm(message)) event.preventDefault();
  });
})();
